import React, { useState, useEffect } from 'react';
import { gatewayApi, battlesApi } from '../api';
import { useActivity } from '../App';
import toast from 'react-hot-toast';

const GATEWAY_ICONS = { RAZORPAY: '💳', PAYTM: '📱', PHONEPE: '🟣', STRIPE: '⚡' };

const ConfigModal = ({ gateway, onClose, onSave }) => {
  const [form, setForm] = useState({ keyId: '', keySecret: '', webhookSecret: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await gatewayApi.configure(gateway.code, form);
      toast.success(`${gateway.name} configuration saved`);
      onSave();
      onClose();
    } catch { toast.error('Failed to save config'); }
    finally { setSaving(false); }
  };

  return (
    <div style={overlayS}>
      <div style={{ ...modalS, maxWidth: '480px' }}>
        <div style={modalHeaderS}>
          <h3 style={{ fontWeight: 700 }}>{GATEWAY_ICONS[gateway.code]} Configure {gateway.name}</h3>
          <button style={closeBtnS} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '24px' }}>
          <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '8px', fontSize: '0.82rem', color: '#f59e0b', marginBottom: '18px' }}>
            ⚠️ Keys are stored in server memory only. For production, configure via environment variables or a secrets vault.
          </div>
          {[
            { key: 'keyId',         label: 'API Key / Key ID',       placeholder: 'rzp_live_xxxxxxxxxxxx' },
            { key: 'keySecret',     label: 'API Secret',              placeholder: '••••••••••••••••' },
            { key: 'webhookSecret', label: 'Webhook Secret (optional)', placeholder: 'whsec_xxxxxxxxx' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: '14px' }}>
              <label style={labelS}>{f.label}</label>
              <input style={inputS} type="password" value={form[f.key]}
                onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.placeholder} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
            <button style={cancelBtnS} onClick={onClose}>Cancel</button>
            <button style={primaryBtnS} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Config'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Payments = () => {
  const { addLog } = useActivity();

  const [gateways, setGateways]   = useState([]);
  const [gwLoading, setGwLoading] = useState(true);
  const [battles, setBattles]     = useState([]);
  const [btLoading, setBtLoading] = useState(true);
  const [configModal, setConfig]  = useState(null);
  const [btPage, setBtPage]       = useState(0);
  const [btTotal, setBtTotal]     = useState(0);
  const [btFilter, setBtFilter]   = useState('');

  useEffect(() => { loadGateways(); loadBattles(); }, []);

  const loadGateways = async () => {
    setGwLoading(true);
    try {
      const res = await gatewayApi.list();
      setGateways(res.data?.data || []);
    } catch { toast.error('Failed to load gateways'); }
    finally { setGwLoading(false); }
  };

  const loadBattles = async (page = 0, status = '') => {
    setBtLoading(true);
    try {
      const res = await battlesApi.getAll({ page, size: 15, status: status || undefined });
      setBattles(res.data?.data || []);
      setBtTotal(res.data?.total || 0);
      setBtPage(page);
    } catch { toast.error('Failed to load battles'); }
    finally { setBtLoading(false); }
  };

  const handleToggle = async (code) => {
    try {
      const res = await gatewayApi.toggle(code);
      const d = res.data?.data;
      setGateways(p => p.map(g => g.code === code ? { ...g, active: d.active } : g));
      toast.success(res.data?.message || 'Gateway toggled');
      addLog('Gateway Toggled', code, 'update');
    } catch { toast.error('Failed to toggle gateway'); }
  };

  // Paid battle stats
  const paidBattles = battles.filter(b => b.entryType === 'PAID' || (b.entryFee && b.entryFee > 0));
  const totalRevenue = paidBattles.reduce((s, b) => s + (b.entryFee || 0), 0);

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Finance</span> / <span className="current">Payments</span></div>
          <h1>Payment Gateway</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Configure payment gateways and view all battle payment activity
          </p>
        </div>
      </div>

      {/* ── Stats Row ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '28px' }}>
        {[
          { label: 'Active Gateways',  value: gateways.filter(g => g.active).length, color: '#22c55e', icon: '✅' },
          { label: 'Total Battles',    value: btTotal,                                 color: 'var(--accent-primary)', icon: '⚔️' },
          { label: 'Paid Battles',     value: paidBattles.length,                      color: '#f59e0b', icon: '💳' },
          { label: 'Entry Fee Pool',   value: `₹${totalRevenue.toLocaleString()}`,     color: '#06b6d4', icon: '💰' },
        ].map((s, i) => (
          <div key={i} style={cardS}>
            <div style={{ fontSize: '1.6rem' }}>{s.icon}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color, marginTop: '6px' }}>{s.value}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Payment Gateways ─────────────────────────────────────────────── */}
      <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '14px' }}>💳 Payment Gateways</h2>
      {gwLoading ? (
        <div style={{ ...cardS, textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading gateways…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px', marginBottom: '32px' }}>
          {gateways.map(gw => (
            <div key={gw.code} style={{ ...cardS, borderTop: `3px solid ${gw.active ? '#22c55e' : 'var(--border-color)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1.8rem' }}>{GATEWAY_ICONS[gw.code] || '🔌'}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{gw.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{gw.description}</div>
                  </div>
                </div>
                <span style={{
                  fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: '20px',
                  background: gw.active ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)',
                  color: gw.active ? '#22c55e' : '#94a3b8',
                }}>
                  {gw.active ? '● ACTIVE' : '○ INACTIVE'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{ ...actionBtnS, flex: 1, justifyContent: 'center',
                    background: gw.active ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                    color: gw.active ? '#ef4444' : '#22c55e',
                    border: `1px solid ${gw.active ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}` }}
                  onClick={() => handleToggle(gw.code)}>
                  {gw.active ? '⏸ Disable' : '▶ Enable'}
                </button>
                <button style={{ ...actionBtnS, flex: 1, justifyContent: 'center' }}
                  onClick={() => setConfig(gw)}>
                  ⚙️ Configure
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Battles Table ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>⚔️ All Battles</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          {['', 'UPCOMING', 'ACTIVE', 'COMPLETED'].map(s => (
            <button key={s}
              style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                background: btFilter === s ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                color:      btFilter === s ? 'white' : 'var(--text-secondary)' }}
              onClick={() => { setBtFilter(s); loadBattles(0, s); }}>
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['ID','Name','Entry Type','Fee (₹)','Status','Start Time','Venue','Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.73rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {btLoading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading…</td></tr>
              ) : battles.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No battles found</td></tr>
              ) : battles.map(b => {
                const statusColor = b.status === 'ACTIVE' ? '#22c55e' : b.status === 'UPCOMING' ? '#6366f1' : '#94a3b8';
                const isPaid = b.entryType === 'PAID' || (b.entryFee && b.entryFee > 0);
                return (
                  <tr key={b.id}
                    style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 14px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>#{b.id}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, fontSize: '0.88rem' }}>{b.name}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '20px', fontWeight: 600,
                        background: isPaid ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)',
                        color: isPaid ? '#f59e0b' : '#22c55e' }}>
                        {isPaid ? '💰 PAID' : '🆓 FREE'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: isPaid ? '#f59e0b' : 'var(--text-muted)' }}>
                      {isPaid ? `₹${b.entryFee}` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: '6px', fontWeight: 600,
                        background: statusColor + '20', color: statusColor }}>
                        {b.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {b.startTime ? new Date(b.startTime).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '0.8rem', color: 'var(--text-secondary)', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.venue || '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {['ACTIVE','COMPLETED','CANCELLED'].map(st => (
                          <button key={st} style={{ ...actionBtnS, fontSize: '0.7rem', padding: '3px 8px',
                            color: st === 'COMPLETED' ? '#22c55e' : st === 'CANCELLED' ? '#ef4444' : '#6366f1' }}
                            onClick={async () => {
                              try {
                                await battlesApi.updateStatus(b.id, st);
                                setBattles(p => p.map(x => x.id === b.id ? { ...x, status: st } : x));
                                addLog('Battle Status Updated', b.name, 'update');
                                toast.success('Battle status updated');
                              } catch { toast.error('Failed'); }
                            }}>
                            {st === 'ACTIVE' ? '▶' : st === 'COMPLETED' ? '✔' : '✕'}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {btTotal > 15 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Total: {btTotal} battles</span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button disabled={btPage === 0} onClick={() => loadBattles(btPage - 1, btFilter)}
                style={{ ...actionBtnS, opacity: btPage === 0 ? 0.4 : 1 }}>‹ Prev</button>
              <button onClick={() => loadBattles(btPage + 1, btFilter)}
                style={actionBtnS}>Next ›</button>
            </div>
          </div>
        )}
      </div>

      {configModal && (
        <ConfigModal gateway={configModal} onClose={() => setConfig(null)} onSave={loadGateways} />
      )}
    </div>
  );
};

const cardS       = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '18px', textAlign: 'center' };
const overlayS    = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' };
const modalS      = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' };
const modalHeaderS= { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-color)' };
const closeBtnS   = { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem' };
const inputS      = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box' };
const labelS      = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' };
const primaryBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' };
const cancelBtnS  = { padding: '9px 18px', borderRadius: '8px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.88rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };
const actionBtnS  = { padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', fontSize: '0.78rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: '4px' };

export default Payments;
